import { HTMLAttributes } from 'react';

/**
 * Avatar — circular identity token. Per the style guide there is ONE avatar
 * treatment: a flat --avatar-bg disc (violet in dark, gold in light) with the
 * first initial in --avatar-text, set in the serif. No gradients.
 *
 * kind: 'member' (default) → flat --avatar-bg disc. Use for every human:
 *                            hosts, travelers, chat, nav, lists.
 *       'alma'             → in production Alma is ALWAYS a circular photo —
 *                            pass `src`. This flat cream-disc treatment is a
 *                            no-photo fallback only, never the shipped state.
 *       'photo'            → renders `src` image (kind ignored when src is set).
 *
 * 'host' and 'user' are accepted as aliases of 'member' for call-site clarity.
 */
export interface AvatarProps extends HTMLAttributes<HTMLElement> {
  /** Identity layer. Default 'member'. */
  kind?: 'member' | 'alma' | 'photo' | 'host' | 'user';
  /** Image URL — when set, renders a photo regardless of kind. */
  src?: string;
  /** Used for the fallback initial. */
  name?: string;
  /** Pixel diameter. Default 40. */
  size?: number;
}

export function Avatar({ kind = 'member', src, name, size = 40, style, ...props }: AvatarProps) {
  const dim = { width: size, height: size, borderRadius: '999px', flexShrink: 0 };
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const fontSize = Math.floor(size * 0.4);
  const center = { display: 'flex', alignItems: 'center', justifyContent: 'center' };

  if (src) {
    return (
      <img
        src={src}
        alt={name || ''}
        style={{ ...dim, objectFit: 'cover', border: '1px solid var(--line)', ...style }}
        {...props}
      />
    );
  }

  if (kind === 'alma') {
    return (
      <div
        style={{
          ...dim, ...center,
          background: 'rgb(var(--cream))',
          border: '1px solid var(--line)',
          color: 'rgb(var(--ink))',
          fontFamily: 'var(--font-serif)',
          fontSize,
          lineHeight: 1,
          ...style,
        }}
        {...props}
      >
        {name ? initial : 'A'}
      </div>
    );
  }

  // member / host / user — the single flat identity disc
  return (
    <div
      style={{
        ...dim, ...center,
        background: 'var(--avatar-bg)',
        color: 'var(--avatar-text)',
        fontFamily: 'var(--font-serif)',
        fontSize,
        lineHeight: 1,
        ...style,
      }}
      {...props}
    >
      {initial}
    </div>
  );
}

export default Avatar;
